import { useEffect, useRef, useState, useCallback } from 'react';
import { ElementType, ElementWithUserNames } from '../../types/element.types';
import { getAllElementsInBreakdownCache } from './getAllElementsInBreakdownCache';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';

/**
 * 重名检查 Hook 元素
 * 用于 ElementSearch 中的元素名称和简称字段的重名检查
 */
export interface ElementDuplicateCheckOptions {
  /** 当前正在编辑的元素ID */
  editingItem: string | null;
  /** 当前正在编辑的字段（'name' | 'description'） */
  editingField: string | null;
  /** 编辑中的值（通过 ref 传递，避免依赖循环） */
  editValuesRef: React.MutableRefObject<{[key: string]: string}>;
  /** 所有场次元素列表（用于获取当前编辑元素的类型等信息） */
  elements: Array<{ elementID: string; elementName?: string; elementAbbr?: string; elementType?: ElementType }>;
  /** 分场ID，用于获取缓存数据 */
  breakdownID: string;
  /** 当前场次元素类型（用于创建时的重名检查） */
  currentElementType: ElementType;
  /** 重名状态更新回调 */
  onDuplicateStateChange?: (hasDuplicate: boolean) => void;
  /** 当前编辑的输入框引用 */
  currentEditingInputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

/**
 * 重名检查 Hook
 * 
 * 功能：
 * 1. 当字段进入编辑状态时，实时检查重名
 * 2. 当输入值变化时，实时检查重名
 * 3. 通过 DOM 属性标记重名状态，供全局快捷键处理器检查
 * 4. 更新输入框的 className 以显示错误样式
 */
export const useElementDuplicateCheck = ({
  editingItem,
  editingField,
  editValuesRef,
  elements,
  breakdownID,
  currentElementType,
  onDuplicateStateChange,
  currentEditingInputRef,
}: ElementDuplicateCheckOptions) => {
  const { getElementCache } = useDataCache();
  // 用于取消未完成的检查
  const cancelTokenRef = useRef<{ cancelled: boolean } | null>(null);
  // 重名状态
  const [hasDuplicate, setHasDuplicate] = useState(false);

  /**
   * 更新 DOM 元素的重名状态
   */
  const updateDuplicateState = (element: HTMLElement | null, hasDuplicate: boolean) => {
    if (!element) return;
    
    if (hasDuplicate) {
      element.setAttribute('data-has-duplicate', 'true');
      element.classList.add('duplicate-error');
    } else {
      element.removeAttribute('data-has-duplicate');
      element.classList.remove('duplicate-error');
    }
  };

  /**
   * 检查重名
   */
  const checkDuplicate = useCallback(async (
    elementId: string | null,
    elementName: string,
    elementAbbr?: string,
    elementType?: ElementType,
    checkField?: 'name' | 'description'
  ): Promise<boolean> => {
    // 如果既没有名称也没有简称，或者没有 breakdownID，返回 false
    if ((!elementName?.trim() && !elementAbbr?.trim()) || !breakdownID) {
      return false;
    }
    
    try {
      // 获取场次表关联的所有元素（优先使用缓存）
      const allElements = await getAllElementsInBreakdownCache(breakdownID, getElementCache);
      
      // 根据 checkField 参数决定检查哪些字段
      // 如果指定了 checkField，只检查对应字段；否则检查所有字段
      let nameDuplicates: ElementWithUserNames[] = [];
      let abbreviationDuplicates: ElementWithUserNames[] = [];

      // 检查元素名称重名（如果提供了名称，且 checkField 为 'name' 或未指定）
      if ((!checkField || checkField === 'name') && elementName?.trim()) {
        nameDuplicates = allElements.filter((item: ElementWithUserNames) => 
          (elementId ? item.elementID !== elementId : true) && // 排除当前编辑的元素（如果有）
          item.elementName?.trim() === elementName.trim() && 
          item.elementType === elementType
        );
      }

      // 检查简称重名（如果提供了简称，且 checkField 为 'description' 或未指定）
      if ((!checkField || checkField === 'description') && elementAbbr?.trim()) {
        abbreviationDuplicates = allElements.filter((item: ElementWithUserNames) => 
          (elementId ? item.elementID !== elementId : true) && // 排除当前编辑的元素（如果有）
          item.elementAbbr?.trim() === elementAbbr.trim() && 
          item.elementType === elementType
        );
      }

      // 合并重名元素
      const allDuplicates = [...nameDuplicates, ...abbreviationDuplicates];
      const uniqueDuplicates = allDuplicates.filter((item, index, self) => 
        index === self.findIndex(t => t.elementID === item.elementID)
      );

      return uniqueDuplicates.length > 0;
    } catch (error) {
      console.error('❌ 重名检查失败:', error);
      return false;
    }
  }, [breakdownID, getElementCache]);

  /**
   * 执行重名检查
   */
  const performCheck = useCallback(async () => {
    // 只有在编辑名称或简称字段时才进行检查
    if (!editingItem || !editingField) {
      return;
    }

    if (editingField !== 'name' && editingField !== 'description') {
      return;
    }

    // 查找当前编辑的元素
    const currentItem = elements.find(item => item.elementID === editingItem);
    if (!currentItem) {
      return;
    }

    // 获取当前输入值（使用 ref 获取最新值）
    const latestEditValues = editValuesRef.current;
    
    const elementName = editingField === 'name' && latestEditValues.name !== undefined
      ? latestEditValues.name 
      : (currentItem.elementName || '');
    
    const elementAbbr = editingField === 'description' && latestEditValues.description !== undefined
      ? latestEditValues.description
      : (currentItem.elementAbbr || '');

    // 查找对应的 DOM 元素（使用 data-item-id 和 data-field）
    const element = document.querySelector(
      `[data-item-id="${editingItem}"][data-field="${editingField}"]`
    ) as HTMLElement | null;

    // 先清除重名状态（乐观更新）
    updateDuplicateState(element, false);
    setHasDuplicate(false);
    onDuplicateStateChange?.(false);

    // 取消之前的检查
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancelled = true;
    }

    // 创建新的取消令牌
    const cancelToken = { cancelled: false };
    cancelTokenRef.current = cancelToken;

    // 根据正在编辑的字段决定只检查哪个字段
    const checkField: 'name' | 'description' | undefined = 
      editingField === 'name' ? 'name' : 
      editingField === 'description' ? 'description' : 
      undefined;

    try {
      // 执行异步检查
      const hasDuplicateResult = await checkDuplicate(
        editingItem,
        elementName,
        elementAbbr,
        currentItem.elementType,
        checkField
      );

      // 如果检查被取消，不更新状态
      if (cancelToken.cancelled) {
        return;
      }

      // 更新状态
      setHasDuplicate(hasDuplicateResult);
      onDuplicateStateChange?.(hasDuplicateResult);

      // 更新 DOM 状态
      const currentElement = document.querySelector(
        `[data-item-id="${editingItem}"][data-field="${editingField}"]`
      ) as HTMLElement | null;
      
      updateDuplicateState(currentElement, hasDuplicateResult);
    } catch (error) {
      console.error('❌ 重名检查失败:', error);
      
      // 如果检查被取消，不更新状态
      if (cancelToken.cancelled) {
        return;
      }

      // 清除错误状态
      setHasDuplicate(false);
      onDuplicateStateChange?.(false);
      
      const currentElement = document.querySelector(
        `[data-item-id="${editingItem}"][data-field="${editingField}"]`
      ) as HTMLElement | null;
      
      updateDuplicateState(currentElement, false);
    }
  }, [editingItem, editingField, elements, editValuesRef, checkDuplicate, onDuplicateStateChange]);

  /**
   * 检查创建时的重名（用于新建输入框）
   */
  const checkDuplicateForCreate = useCallback(async (
    elementName: string,
    elementAbbreviation?: string
  ): Promise<boolean> => {
    return await checkDuplicate(null, elementName, elementAbbreviation, currentElementType);
  }, [checkDuplicate, currentElementType]);

  /**
   * 当编辑状态变化时，执行检查
   */
  useEffect(() => {
    // 如果不在编辑状态，清除所有重名标记
    if (!editingItem || !editingField) {
      setHasDuplicate(false);
      onDuplicateStateChange?.(false);
      // 清除所有可能的重名标记
      document.querySelectorAll('[data-has-duplicate]').forEach(el => {
        el.removeAttribute('data-has-duplicate');
        el.classList.remove('duplicate-error');
      });
      return;
    }

    // 如果不在编辑名称或简称字段，清除重名标记
    if (editingField !== 'name' && editingField !== 'description') {
      setHasDuplicate(false);
      onDuplicateStateChange?.(false);
      const element = document.querySelector(
        `[data-item-id="${editingItem}"][data-field="${editingField}"]`
      ) as HTMLElement | null;
      updateDuplicateState(element, false);
      return;
    }

    // 执行检查
    performCheck();

    // 清理函数：取消未完成的检查
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancelled = true;
      }
    };
  }, [editingItem, editingField, performCheck, onDuplicateStateChange]);

  /**
   * 暴露 performCheck 函数供外部调用（当输入值变化时）
   */
  const triggerCheck = useCallback(() => {
    performCheck();
  }, [performCheck]);

  /**
   * 检查指定字段是否有重名
   * 供外部调用（如全局快捷键处理器）
   */
  const checkHasDuplicate = (itemId: string, field: string): boolean => {
    const element = document.querySelector(
      `[data-item-id="${itemId}"][data-field="${field}"]`
    ) as HTMLElement | null;
    
    if (!element) {
      return false;
    }

    return element.getAttribute('data-has-duplicate') === 'true';
  };

  return {
    hasDuplicate,
    checkHasDuplicate,
    checkDuplicateForCreate,
    triggerCheck,
  };
};

